import React, { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import PathPickMembershipIntroBanner from "@/features/pathpick/features/membership/components/banner-section/PathPickMembershipIntroBanner";
import PathPickMembershipPromoBanner from "@/features/pathpick/features/membership/components/banner-section/PathPickMembershipPromoBanner";
import PathPickMembershipFreeTrialBanner from "@/features/pathpick/features/membership/components/banner-section/PathPickMembershipFreeTrialBanner";
import PathPickMembershipCurrentPlanCard from "@/features/pathpick/features/membership/components/PathPickMembershipCurrentPlanCard";
import PathPickMembershipPlanCompareSection from "@/features/pathpick/features/membership/components/plan-compare-section/PathPickMembershipPlanCompareSection";
import PathPickMembershipApiIntegrationBanner from "@/features/pathpick/features/membership/components/banner-section/PathPickMembershipApiIntegrationBanner";
import PathPickMembershipPlanChangeModal from "@/features/pathpick/features/membership/components/plan-change/PathPickMembershipPlanChangeModal";
import PathPickMembershipFreeTrialModal from "@/features/pathpick/features/membership/components/PathPickMembershipFreeTrialModal";
import PathPickMembershipPendingApplicationModal from "@/features/pathpick/features/membership/components/PathPickMembershipPendingApplicationModal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import useGetPathPickMembershipApplications from "@/features/pathpick/features/membership/hooks/query/useGetPathPickMembershipApplications";
import useGetPathPickMembershipPlans from "@/features/pathpick/features/membership/hooks/query/useGetPathPickMembershipPlans";
import {
  matchesPlanCode,
  MEMBERSHIP_COLORS,
  MEMBERSHIP_PLANS,
} from "@/features/pathpick/features/membership/constants";
import {
  MembershipBillingCycle,
  MembershipPlan,
} from "@/features/pathpick/features/membership/types";
import { Helmet } from "react-helmet-async";

function PathPickMembershipPage() {
  const { t, tTitle } = useI18nHelper();
  // 라벨이 이미 "패스픽 멤버십"이라 접두사를 붙이지 않는다.
  const pageTitle = tTitle("common:navigation.path_pick_membership");
  const { planKey, membership, membershipStatus, hasUsedFreeTrial } =
    useMembership();
  // 입금 대기 중인 신청 — 그 플랜 카드만 "신청 중"으로 바뀐다.
  const { pendingApplication } = useGetPathPickMembershipApplications();
  // 무료체험(FREE) 신청에 쓸 서버 플랜 id.
  const { findPlanIdByCode } = useGetPathPickMembershipPlans();

  const [billingCycle, setBillingCycle] =
    useState<MembershipBillingCycle>("monthly");
  // 플랜 변경 모달에 띄울 대상 플랜 (null이면 닫힘)
  const [changeTargetPlan, setChangeTargetPlan] =
    useState<MembershipPlan | null>(null);
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  // 입금 대기 중인 신청의 입금 정보·접수 내용 요약
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);

  const isSubscribed = membershipStatus === "SUBSCRIBED";
  // 첫 달 50% 프로모션 — 유료 첫 결제 전(결제 전/체험 중/체험 만료)에 붙는다(시안).
  // 유료를 쓰다 만료된 재가입(SUBSCRIPTION_EXPIRED)은 정가다.
  const isFirstSubscription =
    membershipStatus === "NONE" ||
    membershipStatus === "TRIAL" ||
    membershipStatus === "TRIAL_EXPIRED";
  // 현재 이용 중인 플랜 — 유료 구독 중일 때만. 무료체험(FREE)은 비교표에서 미보유 취급이라
  // 카드 CTA가 전부 "플랜 시작하기"로 나온다(시안).
  const currentPlan = isSubscribed
    ? MEMBERSHIP_PLANS.find((plan) => plan.id === planKey)
    : undefined;

  // 무료체험 진입 조건: 미소진 + 현재 이용 중인 멤버십 없음 + 서버에 FREE 플랜이 활성.
  // 상태가 아니라 소진 여부가 기준이다 — 멤버십이 만료된 바이어도 체험을 안 썼다면 가능하고,
  // 체험 만료(TRIAL_EXPIRED)는 소진 이력이 있어 자연히 제외된다.
  const freeTrialPlanId = findPlanIdByCode("FREE");
  const canStartFreeTrial =
    !hasUsedFreeTrial &&
    membershipStatus !== "TRIAL" &&
    membershipStatus !== "SUBSCRIBED" &&
    freeTrialPlanId != null;

  const pendingPlanCode = pendingApplication?.plan?.code ?? null;

  // 중복 신청을 허용한다 — 이미 접수한 플랜만 접수 내용(입금 계좌 포함)을 다시 보여주고,
  // 나머지 플랜은 평소대로 새 신청을 받는다.
  const handleChangeRequest = (plan: MembershipPlan) => {
    if (matchesPlanCode(plan, pendingPlanCode)) {
      setIsPendingModalOpen(true);
      return;
    }
    setChangeTargetPlan(plan);
  };

  return (
    <Box
      sx={{
        padding: "16px",
        overflow: "auto",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      <Flex flexDirection={"column"} gap={"16px"}>
        <Flex flexDirection={"column"} gap={"4px"}>
          {/* 구독 중에는 "현재 플랜 대비 비교", 그 외 상태는 "플랜 고르기" 헤더(시안) */}
          <Text
            textStyle={"body-lg-bold"}
            fontSize={{ base: "22px", md: "30px" }}
            lineHeight={{ base: "30px", md: "36px" }}
            color={MEMBERSHIP_COLORS.textStrong}
          >
            {t(
              isSubscribed
                ? "membership:page.title"
                : "membership:page.title_new"
            )}
          </Text>
          <Text
            textStyle={"body-sm-regular"}
            color={MEMBERSHIP_COLORS.textMuted}
          >
            {t(
              isSubscribed
                ? "membership:page.description"
                : "membership:page.description_new"
            )}
          </Text>
        </Flex>
        {isFirstSubscription && <PathPickMembershipPromoBanner />}
        <PathPickMembershipIntroBanner />
        {canStartFreeTrial && (
          <PathPickMembershipFreeTrialBanner
            onStart={() => setIsTrialModalOpen(true)}
          />
        )}
        {isSubscribed && (
          <PathPickMembershipCurrentPlanCard
            planName={currentPlan?.englishName ?? membership?.name ?? ""}
            renewalDay={membership?.renewalDay ?? null}
          />
        )}
        <PathPickMembershipPlanCompareSection
          billingCycle={billingCycle}
          onChangeBillingCycle={setBillingCycle}
          currentPlan={currentPlan}
          isFirstSubscription={isFirstSubscription}
          onChangeRequest={handleChangeRequest}
          onStartTrial={
            canStartFreeTrial ? () => setIsTrialModalOpen(true) : undefined
          }
          pendingPlanCode={pendingPlanCode}
        />
        {/*<PathPickMembershipApiIntegrationBanner />*/}
      </Flex>
      {changeTargetPlan && (
        <PathPickMembershipPlanChangeModal
          isOpen
          onClose={() => setChangeTargetPlan(null)}
          targetPlan={changeTargetPlan}
          currentPlan={currentPlan}
          billingCycle={billingCycle}
          onChangeBillingCycle={setBillingCycle}
          isFirstSubscription={isFirstSubscription}
          onApplied={() => {
            setChangeTargetPlan(null);
            setIsPendingModalOpen(true);
          }}
        />
      )}
      {/* 입금 대기 신청 요약 — 대기 중 재진입과 신청 직후 양쪽에서 띄운다.
          접수 내용은 모달이 신청 내역 API에서 직접 읽는다. */}
      <PathPickMembershipPendingApplicationModal
        isOpen={isPendingModalOpen}
        onClose={() => setIsPendingModalOpen(false)}
      />
      {/* 무료체험 신청 — 체험 조건 + 사업자 정보(등록증 없으면 여기서 등록) + 세금계산서.
          신청은 모달이 직접 처리한다(즉시 승인 → me 재검증으로 TRIAL 전환). */}
      <PathPickMembershipFreeTrialModal
        isOpen={isTrialModalOpen}
        onClose={() => setIsTrialModalOpen(false)}
      />
    </Box>
  );
}

export default PathPickMembershipPage;
